// ===========================================
// Enquiry React Query Hooks
// ===========================================
// The staff pipeline (enquiries plan §5.7). One key factory; every mutation invalidates the
// lists, the counts, the detail, its history and the admin dashboard (its pending count and
// funnel read the stage), so no screen shows a stage that has moved.

import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { apiErrorCode, apiErrorMessage } from '../utils/apiError';
import {
  enquiryApi,
  enquiryPublicApi,
  type EnquiryFilters,
  type EnquiryListKind,
  type EnquirySettingsPatch,
  type FamilyRepliedData,
  type LinkFamilyData,
  type LogActivityData,
  type ManualEnquiryData,
  type UpdateEnquiryData,
  type PublicEnquirySubmitData,
  type ReviewOutcome,
  type SetNextActionData,
  type TransitionEnquiryData,
} from '../api/enquiry.api';
import { dashboardKeys } from './useDashboard';
import { ENQUIRY_KEY_ROOT, SETTLING_IN_KEY_ROOT } from './enquiryKeyRoots';
import { reportsKeys } from './useReports';
import { meetAndGreetKeys, MNG_NOTES_KEY_ROOT } from './useMeetAndGreet';
import { calendarKeys } from './useHybridBooking';
import { isSpamCheckFailed } from './useTurnstileGate';

// The shared error readers (utils/apiError.ts), re-exported for the enquiry screens.
export { apiErrorCode, apiErrorMessage };

// ===========================================
// QUERY KEYS
// ===========================================

export const enquiryKeys = {
  all: ENQUIRY_KEY_ROOT,
  lists: () => [...enquiryKeys.all, 'list'] as const,
  list: (filters?: EnquiryFilters) => [...enquiryKeys.lists(), filters ?? {}] as const,
  details: () => [...enquiryKeys.all, 'detail'] as const,
  detail: (id: string) => [...enquiryKeys.details(), id] as const,
  stats: () => [...enquiryKeys.all, 'stats'] as const,
  flags: () => [...enquiryKeys.all, 'flags'] as const,
  config: () => [...enquiryKeys.all, 'config'] as const,
  owners: () => [...enquiryKeys.all, 'owners'] as const,
  options: (kind: EnquiryListKind, opts: { includeHidden?: boolean; forNewEnquiry?: boolean } = {}) =>
    [...enquiryKeys.all, 'options', kind, opts] as const,
  review: () => [...enquiryKeys.all, 'migration-review'] as const,
  templates: (opts: { includeHidden?: boolean } = {}) => [...enquiryKeys.all, 'reply-templates', opts] as const,
  settings: () => [...enquiryKeys.all, 'settings'] as const,
  matches: (probe: { email?: string; phone?: string; excludeEnquiryId?: string }) => [...enquiryKeys.all, 'matches', probe] as const,
};

type ApiError = Error & { response?: { data?: { message?: string; details?: { code?: string } } } };

// The Onboarding check-in's key root lives in the leaf `enquiryKeyRoots.ts` (`useSettlingIn.ts`
// imports this module and `useLessons.ts` needs the root: an import back would be a cycle).
export { SETTLING_IN_KEY_ROOT };

/**
 * Everything an enquiry write can change (`enquiryKeys.all` covers lists, counts, details, history,
 * review). The Onboarding check-in rows too: each carries its enquiries' stages, and the settings
 * hold the onboarding window every due day is derived from (/qa F-M1).
 */
function invalidateEnquiries(queryClient: QueryClient): void {
  queryClient.invalidateQueries({ queryKey: enquiryKeys.all });
  queryClient.invalidateQueries({ queryKey: dashboardKeys.admin.all });
  queryClient.invalidateQueries({ queryKey: SETTLING_IN_KEY_ROOT });
  // The funnel counts "got at least this far" from the stage history (F-L2).
  queryClient.invalidateQueries({ queryKey: [...reportsKeys.operations.all, 'enquiry-funnel'] });
}

/**
 * The pipeline config is derived from more than the Enquiries section: the school's zone and office
 * days (School Info) and the "Meet & Greet required" switch (Student Onboarding) feed the targets
 * and the pre-filled due days (review 5.7 L7). Those saves call this.
 */
export function invalidateEnquiryConfig(queryClient: QueryClient): void {
  queryClient.invalidateQueries({ queryKey: enquiryKeys.config() });
}

// ===========================================
// ADMIN QUERY HOOKS
// ===========================================

export function useEnquiries(filters?: EnquiryFilters, enabled = true) {
  return useQuery({
    queryKey: enquiryKeys.list(filters),
    queryFn: () => enquiryApi.list(filters),
    enabled,
  });
}

export function useEnquiry(id: string) {
  return useQuery({
    queryKey: enquiryKeys.detail(id),
    queryFn: () => enquiryApi.get(id),
    enabled: !!id,
  });
}

export function useEnquiryStats() {
  return useQuery({
    queryKey: enquiryKeys.stats(),
    queryFn: () => enquiryApi.getStats(),
  });
}

/** The flag counts (plan §10). `enabled` is the caller's `can('enquiries', 'read')`: never fetched without it. */
export function useEnquiryFlags(enabled = false) {
  return useQuery({
    queryKey: enquiryKeys.flags(),
    queryFn: () => enquiryApi.getFlags(),
    enabled,
  });
}

/** Stages, labels, owners, what a move pre-fills, and the school's today. */
export function useEnquiryConfig(enabled = true) {
  return useQuery({
    queryKey: enquiryKeys.config(),
    queryFn: () => enquiryApi.getConfig(),
    staleTime: 60_000,
    enabled,
  });
}

/** Rows per history page. */
export const ACTIVITY_PAGE_SIZE = 50;

/**
 * The history, newest first, a page at a time: `fetchNextPage` loads older rows ("Show older",
 * review 5.7 L6). `data.pages` concatenated is the history so far; `pages[0].total` the full count.
 */
export function useEnquiryActivities(id: string) {
  return useInfiniteQuery({
    queryKey: [...enquiryKeys.detail(id), 'activities'] as const,
    queryFn: ({ pageParam }) => enquiryApi.activities(id, { page: pageParam, limit: ACTIVITY_PAGE_SIZE }),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.page * last.limit < last.total ? last.page + 1 : undefined),
    enabled: !!id,
  });
}

/** Everyone who may own an enquiry (names only). */
export function useAssignableOwners(enabled = true) {
  return useQuery({
    queryKey: enquiryKeys.owners(),
    queryFn: () => enquiryApi.assignableOwners(),
    enabled,
    staleTime: 60_000,
  });
}

export function useEnquiryOptions(
  kind: EnquiryListKind,
  opts: { includeHidden?: boolean; forNewEnquiry?: boolean } = {},
  enabled = true,
) {
  return useQuery({
    queryKey: enquiryKeys.options(kind, opts),
    queryFn: () => enquiryApi.listOptions(kind, opts),
    enabled,
  });
}

/** The migrated "Resolved" rows waiting for an outcome (needs enquiries.write). */
export function useMigrationReview(enabled = true) {
  return useQuery({
    queryKey: enquiryKeys.review(),
    queryFn: () => enquiryApi.migrationReview(),
    enabled,
  });
}

/** Enough of a phone number to match on (the server's MIN_PHONE_DIGITS). */
const MIN_PHONE_DIGITS = 6;
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Other enquiries with this email or phone, and the family the email belongs to (warn only).
 * Asks only once there is something worth matching: a whole email, or six digits of a phone.
 * Pass already-debounced values.
 */
export function useEnquiryMatches(
  probe: { email?: string | null; phone?: string | null; excludeEnquiryId?: string },
  enabled = true,
) {
  const email = probe.email?.trim() && EMAIL_SHAPE.test(probe.email.trim()) ? probe.email.trim() : undefined;
  const phone = (probe.phone ?? '').replace(/\D/g, '').length >= MIN_PHONE_DIGITS ? probe.phone!.trim() : undefined;
  const key = { email, phone, excludeEnquiryId: probe.excludeEnquiryId };
  return useQuery({
    queryKey: enquiryKeys.matches(key),
    queryFn: () => enquiryApi.matches(key),
    enabled: enabled && (email !== undefined || phone !== undefined),
    staleTime: 30_000,
  });
}

/** Settings → Enquiries (needs system_settings.read). */
export function useEnquirySettings(enabled = true) {
  return useQuery({
    queryKey: enquiryKeys.settings(),
    queryFn: () => enquiryApi.getSettings(),
    enabled,
  });
}

// ===========================================
// PUBLIC MUTATION HOOKS
// ===========================================

/**
 * Submit a public enquiry form
 */
export function useSubmitEnquiry() {
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: PublicEnquirySubmitData) =>
      enquiryPublicApi.submit(data),
    onError: (error: ApiError) => {
      // #412: the page explains a spam-check refusal inline; no second message here.
      if (isSpamCheckFailed(error)) return;
      enqueueSnackbar(apiErrorMessage(error, 'Failed to submit enquiry. Please try again.'), { variant: 'error' });
    },
  });
}

// ===========================================
// ADMIN MUTATION HOOKS
// ===========================================

interface PipelineOptions<TResult> {
  /**
   * The caller shows every error inline (a dialog's Alert), so no snackbar as well: one message
   * per refusal (review 5.7 L3). The caches are still refreshed.
   */
  quietErrors?: boolean;
  /** Extra caches the write changes (e.g. the Meet & Greet list after a link). */
  alsoInvalidate?: ReadonlyArray<readonly unknown[]>;
  /** A success message computed from the result (overrides `messages.success`). */
  successFrom?: (result: TResult) => { message: string; variant: 'success' | 'info' | 'warning' } | null;
}

/** A pipeline mutation: invalidate everything, say what happened, and show the server's reason. */
function usePipelineMutation<TVars, TResult>(
  mutationFn: (vars: TVars) => Promise<TResult>,
  messages: { success?: string; error: string },
  opts: PipelineOptions<TResult> = {},
) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const refresh = () => {
    invalidateEnquiries(queryClient);
    for (const queryKey of opts.alsoInvalidate ?? []) queryClient.invalidateQueries({ queryKey });
  };
  return useMutation({
    mutationFn,
    onSuccess: (result: TResult) => {
      refresh();
      const said = opts.successFrom ? opts.successFrom(result) : null;
      if (said) enqueueSnackbar(said.message, { variant: said.variant, ...(said.variant === 'warning' ? { persist: true } : {}) });
      else if (messages.success) enqueueSnackbar(messages.success, { variant: 'success' });
    },
    onError: (error: ApiError) => {
      // A 409 "moved by someone else" still refreshes, so the reload the message asks for is done.
      refresh();
      if (opts.quietErrors) return;
      enqueueSnackbar(apiErrorMessage(error, messages.error), { variant: 'error' });
    },
  });
}

/** Move the stage. `quietErrors` when the dialog shows the server's reason inline. */
export function useTransitionEnquiry(opts: { quietErrors?: boolean } = {}) {
  return usePipelineMutation(
    ({ id, data }: { id: string; data: TransitionEnquiryData }) => enquiryApi.transition(id, data),
    { success: 'Stage moved', error: 'Failed to move the stage' },
    // The Meet & Greet page's "Enquiry: <name> · <stage>" chip shows this stage.
    { quietErrors: opts.quietErrors, alsoInvalidate: [meetAndGreetKeys.lists()] },
  );
}

export function useSetNextAction(opts: { quietErrors?: boolean } = {}) {
  return usePipelineMutation(
    ({ id, data }: { id: string; data: SetNextActionData }) => enquiryApi.setNextAction(id, data),
    { success: 'Next action saved', error: 'Failed to save the next action' },
    { quietErrors: opts.quietErrors },
  );
}

export function useArchiveEnquiry() {
  return usePipelineMutation(
    ({ id, archived }: { id: string; archived: boolean }) => (archived ? enquiryApi.archive(id) : enquiryApi.unarchive(id)),
    { error: 'Failed to change the archive' },
  );
}

export function useReviewOutcome() {
  return usePipelineMutation(
    ({ id, outcome, familyId }: { id: string; outcome: ReviewOutcome; familyId?: string }) =>
      enquiryApi.reviewOutcome(id, { outcome, familyId }),
    { success: 'Outcome saved', error: 'Failed to save the outcome' },
    { alsoInvalidate: [meetAndGreetKeys.lists()] },
  );
}

/** A link changes up to two bookings' `enquiryId`, so the Meet & Greet lists refresh too (review 5.7 M2). */
export function useLinkMeetAndGreet() {
  return usePipelineMutation(
    ({ meetAndGreetId, enquiryId }: { meetAndGreetId: string; enquiryId: string }) =>
      enquiryApi.linkMeetAndGreet(meetAndGreetId, enquiryId),
    { success: 'Meet & Greet linked', error: 'Failed to link the Meet & Greet' },
    // A link can cancel the replaced unconfirmed booking (Phase 3 L4): the calendar and the notes
    // caches move too, so use the same list every Meet & Greet write uses (whole-feature review L3).
    { alsoInvalidate: [meetAndGreetKeys.all, calendarKeys.all, MNG_NOTES_KEY_ROOT] },
  );
}

/**
 * The signed booking link for "Insert M&G link". A read, fetched on click. A 409
 * `ENQUIRY_ALREADY_LINKED` means the panel was stale (a booking arrived since it loaded), so the
 * enquiry is refetched and the panel hides the button; the caller shows the reason.
 */
export function useEnquiryBookingLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => enquiryApi.bookingLink(id),
    onError: (error: ApiError) => {
      if (apiErrorCode(error) === 'ENQUIRY_ALREADY_LINKED') queryClient.invalidateQueries({ queryKey: enquiryKeys.all });
    },
  });
}

/** What the office is told after a reply, from the server's `emailSent` (review 5.7 M6). */
export function replyOutcomeMessage(emailSent: boolean | null): { message: string; variant: 'success' | 'info' | 'warning' } {
  if (emailSent === false) {
    return {
      message: 'Saved to the history, but the email could not be sent. The family has NOT received it.',
      variant: 'warning',
    };
  }
  if (emailSent === null) return { message: 'Note saved', variant: 'success' };
  return { message: 'Reply sent', variant: 'success' };
}

/**
 * Reply to an enquiry, by email or kept as a note. A failed email is a persistent warning, never
 * "saved": the family has not heard back.
 */
export function useReplyToEnquiry() {
  return usePipelineMutation(
    ({ id, message, sentViaEmail = true }: { id: string; message: string; sentViaEmail?: boolean }) =>
      enquiryApi.reply(id, { message, sentViaEmail }),
    { error: 'Failed to send reply' },
    { successFrom: (result) => replyOutcomeMessage(result.emailSent) },
  );
}

/**
 * Delete an enquiry (the owner tier only)
 */
export function useDeleteEnquiry() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (id: string) => enquiryApi.delete(id),
    onSuccess: () => {
      invalidateEnquiries(queryClient);
      // The FK nulls a linked booking's enquiryId.
      queryClient.invalidateQueries({ queryKey: meetAndGreetKeys.all });
      enqueueSnackbar('Enquiry deleted', { variant: 'info' });
    },
    onError: (error: ApiError) => {
      enqueueSnackbar(apiErrorMessage(error, 'Failed to delete enquiry'), { variant: 'error' });
    },
  });
}

/**
 * Create a manual enquiry (logged by staff on behalf of a contact)
 */
export function useCreateManualEnquiry() {
  return usePipelineMutation(
    (data: ManualEnquiryData) => enquiryApi.createManual(data),
    { success: 'Enquiry created', error: 'Failed to create enquiry' },
  );
}

/** PATCH /:id: details only. `quietErrors` when the dialog shows the server's reason inline. */
export function useUpdateEnquiry(opts: { quietErrors?: boolean } = {}) {
  return usePipelineMutation(
    ({ id, data }: { id: string; data: UpdateEnquiryData }) => enquiryApi.update(id, data),
    { success: 'Details saved', error: 'Failed to save the details' },
    { quietErrors: opts.quietErrors },
  );
}

/** "+ Call", "+ Note". */
export function useLogEnquiryActivity(opts: { quietErrors?: boolean } = {}) {
  return usePipelineMutation(
    ({ id, data }: { id: string; data: LogActivityData }) => enquiryApi.logActivity(id, data),
    { success: 'Saved to the history', error: 'Failed to save to the history' },
    { quietErrors: opts.quietErrors },
  );
}

/** The ball comes back to the school; a Not Proceeding enquiry reopens. */
export function useFamilyReplied() {
  return usePipelineMutation(
    ({ id, data }: { id: string; data: FamilyRepliedData }) => enquiryApi.familyReplied(id, data),
    { success: 'Recorded: the family replied', error: 'Failed to record the reply' },
  );
}

/** Link a family or a student (A6: the enquiry moves to Onboarding). */
export function useLinkEnquiryFamily() {
  return usePipelineMutation(
    ({ id, data }: { id: string; data: LinkFamilyData }) => enquiryApi.linkFamily(id, data),
    { error: 'Failed to link the family' },
    { successFrom: (r) => ({ message: r.alreadyLinked ? 'Already linked' : 'Family linked', variant: r.alreadyLinked ? 'info' : 'success' }) },
  );
}

// ---- The editable lists ----

function useOptionMutation<TVars, TResult>(mutationFn: (vars: TVars) => Promise<TResult>, error: string) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn,
    // Rows and the detail carry the option's label themselves, so a rename must refresh them too.
    onSuccess: () => invalidateEnquiries(queryClient),
    onError: (e: ApiError) => {
      // "The list changed. Reload it": reload it, or the next click fails the same way (Phase 7 review N6).
      if (apiErrorCode(e) === 'OPTION_ORDER_STALE') invalidateEnquiries(queryClient);
      enqueueSnackbar(apiErrorMessage(e, error), { variant: 'error' });
    },
  });
}

/** Any staff member with enquiries.write. Resolves to `{ option, existed }`. */
export function useAddEnquiryOption() {
  return useOptionMutation(
    ({ kind, label }: { kind: EnquiryListKind; label: string }) => enquiryApi.addOption(kind, label),
    'Failed to add the option',
  );
}

/** The owner tier only. */
export function useUpdateEnquiryOption() {
  return useOptionMutation(
    ({ optionId, patch }: { optionId: string; patch: { label?: string; isActive?: boolean } }) => enquiryApi.updateOption(optionId, patch),
    'Failed to update the option',
  );
}

/** The owner tier only. */
export function useReorderEnquiryOptions() {
  return useOptionMutation(
    ({ kind, orderedIds }: { kind: EnquiryListKind; orderedIds: string[] }) => enquiryApi.reorderOptions(kind, orderedIds),
    'Failed to reorder the list',
  );
}

// ---- Saved reply templates (Phase 7) ----

/** The active templates (the reply box), or with `includeHidden` the owner's full list (settings). */
export function useReplyTemplates(opts: { includeHidden?: boolean } = {}, enabled = true) {
  return useQuery({
    queryKey: enquiryKeys.templates(opts),
    queryFn: () => enquiryApi.listTemplates(opts),
    enabled,
  });
}

function useTemplateMutation<TVars, TResult>(mutationFn: (vars: TVars) => Promise<TResult>, success: string | null, error: string) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn,
    // Only the template lists show a template: the reply box's and the settings editor's.
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...enquiryKeys.all, 'reply-templates'] });
      if (success) enqueueSnackbar(success, { variant: 'success' });
    },
    onError: (e: ApiError) => {
      // "The list changed. Reload it": reload it (review N6).
      if (apiErrorCode(e) === 'TEMPLATE_ORDER_STALE') queryClient.invalidateQueries({ queryKey: [...enquiryKeys.all, 'reply-templates'] });
      enqueueSnackbar(apiErrorMessage(e, error), { variant: 'error' });
    },
  });
}

/** Anyone with enquiries.write. */
export function useAddReplyTemplate() {
  return useTemplateMutation((data: { name: string; body: string }) => enquiryApi.addTemplate(data), 'Template saved', 'Failed to save the template');
}

/** The owner tier only. */
export function useUpdateReplyTemplate() {
  return useTemplateMutation(
    ({ templateId, patch }: { templateId: string; patch: { name?: string; body?: string; isActive?: boolean } }) =>
      enquiryApi.updateTemplate(templateId, patch),
    null,
    'Failed to update the template',
  );
}

/** The owner tier only. */
export function useReorderReplyTemplates() {
  return useTemplateMutation((orderedIds: string[]) => enquiryApi.reorderTemplates(orderedIds), null, 'Failed to reorder the templates');
}

/** Fill a template for one enquiry (writes nothing). The caller shows its own error. */
export function useRenderReplyTemplate() {
  return useMutation({
    mutationFn: ({ enquiryId, templateId }: { enquiryId: string; templateId: string }) => enquiryApi.renderTemplate(enquiryId, templateId),
  });
}

// ---- Settings → Enquiries ----

export function useUpdateEnquirySettings() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: (patch: EnquirySettingsPatch) => enquiryApi.updateSettings(patch),
    onSuccess: () => {
      // The config (labels, owners, defaults) is derived from the settings; the dashboard shows stages.
      invalidateEnquiries(queryClient);
      enqueueSnackbar('Enquiry settings saved', { variant: 'success' });
    },
    onError: (e: ApiError) => enqueueSnackbar(apiErrorMessage(e, 'Failed to save the enquiry settings'), { variant: 'error' }),
  });
}

export function useResetEnquirySettings() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: () => enquiryApi.resetSettings(),
    onSuccess: () => {
      invalidateEnquiries(queryClient);
      enqueueSnackbar('Enquiry settings reset to defaults', { variant: 'success' });
    },
    onError: (e: ApiError) => enqueueSnackbar(apiErrorMessage(e, 'Failed to reset the enquiry settings'), { variant: 'error' }),
  });
}
